import cx from "classnames";
import React from "react";

import { FeatureItem } from "@/mk2/apps/strollers/components/FeatureItem";
import { FEATURES_LIST } from "@/mk2/apps/strollers/features";
import { Feature } from "@/mk2/apps/strollers/features/basic";
import { strollersListUrl } from "@/mk2/apps/strollers/urls";
import { isSiblingType } from "@/mk2/apps/strollers/utils";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import { Features, StrollerBrandEntity, VariantAttributesWithOptions } from "@/mk2/schemas";
import {
    STROLLER_BRAND,
    STROLLER_HIDE_FEATURES,
    STROLLER_MORE_FEATURES,
} from "@/mk/autogenerated/translation";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./FeaturesList.mscss";

interface OwnProps {
    className?: string;
    brand: StrollerBrandEntity;
    features: Features;
    variantAttributes: VariantAttributesWithOptions;
}

interface OwnState {
    expanded: boolean;
}

export const LISTED_FEATURE_NAMES = [
    "type",
    "weight_in_kg",
    "unfolded_dimensions_in_cm",
    "folded_dimensions_in_cm",
    "origin_countries",
    "maximum_load_per_child_in_kg",
    "recommended_age",
    "back_support_can_be_in_vertical_seat_position",
    "rotating_seat",
    "bucket_seat",
    "platform_material",
    "can_be_converted_to_a_double",
    "maximum_load_per_1st_child_in_kg",
    "maximum_load_per_2nd_child_in_kg",
    "second_seat_recommended_age",
    "fully_fledged_second_seat",
    "can_be_converted_to_a_single",
    "rotating_second_seat",
    "memory_button",
    "bucket_second_seat",
    "back_support_can_be_positioned",
    "back_support_can_be_in_lying_position",
    "back_support_positioning_type",
    "one_hand_positioning",
    "back_support_positions",
    "back_support_can_be_extended",
    "leg_rest_can_be_positioned",
    "leg_rest_positions",
    "leg_rest_can_be_extended",
    "back_support_length_in_cm",
    "seat_dims_in_cm",
    "seat_surface_total_length_in_cm",
    "higher_seat_position",
    "height_adjustable_seat",
    "type_2in1_seat",
    "seat_fabrics_can_be_taken_off",
    "second_seat_back_support_can_be_positioned",
    "second_seat_back_support_positioning_type",
    "second_seat_one_hand_positioning",
    "second_seat_back_support_positions",
    "second_seat_leg_rest",
    "second_seat_back_support_length_in_cm",
    "second_seat_dims_in_cm",
    "second_seat_surface_total_length_in_cm",
    "roof_present",
    "extendable_hood",
    "hood_features_a_sun_visor",
    "follow_the_sun_hood",
    "removable_hood",
    "height_adjustable_hood",
    "peek_a_boo_window_in_the_hood",
    "closable_peek_a_boo_window",
    "peek_a_boo_window_with_silent_closure",
    "waterproof_hood",
    "hood_equipped_with_uv_protection",
    "hood_equipped_with_ventilation_panel",
    "seat_unit_stroller_body_equipped_with_ventilation_system",
    "second_seat_roof_present",
    "bumper_bar",
    "bumper_bar_type",
    "bumper_bar_can_be_removed",
    "swing_away_bumper_bar",
    "bumper_bar_can_be_positioned",
    "second_seat_bumper_bar",
    "number_of_wheels",
    "variant_front_wheel_type",
    "variant_rear_wheel_type",
    ACTIVE_SERVER_ID === 201 ? "wheel_description_sk" : "wheel_description_cz",
    "front_wheels_diameter_in_cm",
    "rear_wheels_diameter_in_cm",
    "wheels_can_be_removed",
    "all_wheels_are_double",
    "number_of_double_wheels",
    "possibility_of_terrain_wheels",
    "suspension",
    "suspended_wheels",
    "suspension_type",
    "swinging",
    "adjustable_suspension",
    "central_brake_present",
    "central_brake_system",
    "manual_brake_present",
    "folding_system",
    "one_hand_folding",
    "checked_baggage",
    "folds_with_the_seat_attached",
    "folds_with_the_seat_in_reverse",
    "folds_with_both_seats_attached",
    "folds_with_seat_outwards",
    "standing_fold",
    "autolock",
    "can_be_pulled_behind",
    "folds_by_itself",
    "has_carry_handle",
    "adjustable_handle",
    "type_of_handle_adjustment",
    "foldable_handrail",
    "handle_material",
    "handle_height_in_cm",
    "shopping_basket",
    "big_shopping_basket",
    "shopping_basket_capacity_in_kg",
    "shopping_basket_closable",
    "carrycot_can_be_attached",
    "carrycot_dims_in_cm",
    "carrycot_has_handle",
    "inclinable_carrycot",
    "foldable_carrycot",
    "carrycot_with_cradle",
    "carrycot_has_ventilation",
    "carrycot_usable_in_car",
    "car_seat_can_be_attached",
    "car_seat_has_to_be_bought",
    "car_seat_compatibility_most_brands",
    "car_seat_compatibility_roemer",
    "two_car_seats_can_be_attached",
    "parent_tray_organizer",
    "cupholder_possibility",
    "retroreflector",
    "supplied_cape",
    "supplied_mosquito_net",
    "supplied_footmuff",
    "supplied_pad",
    "supplied_changing_bag",
    "supplied_transport_bag",
    "motorized",
    "led_lighting",
    "discontinued_year",
    "hood_equipped_with_a_pocket",
    "stroller_body_equipped_with_a_pocket",
    "harness_type",
    "harness_padding",
    "height_adjustable_harness",
    "bumper_bar_equipped_with_a_removable_cover",
    "bumper_bar_can_be_bought_separately",
    "cleaning_the_stroller_fabrics",
    "wrist_safety_strap",
    "higher_positioned_carrycot",
    "two_wheel_mode",
    "carrycot_with_ventilation_window_on_roof",
    "carrycot_with_panoramic_window_on_side",
];

const alwaysShownForSiblings = {
    second_seat_back_support_can_be_positioned: true,
    two_car_seats_can_be_attached: true,
};

const alwaysShown = {
    back_support_can_be_in_lying_position: true,
    back_support_can_be_positioned: true,
    bumper_bar: true,
    car_seat_can_be_attached: true,
    carrycot_can_be_attached: true,
    leg_rest_can_be_positioned: true,
    number_of_wheels: true,
    one_hand_positioning: true,
    recommended_age: true,
    roof_present: true,
    back_support_can_be_in_vertical_seat_position: true,
    rotating_seat: true,
    shopping_basket: true,
    suspension: true,
    type: true,
    weight_in_kg: true,
    unfolded_dimensions_in_cm: true,
    folded_dimensions_in_cm: true,
    origin_countries: true,
    maximum_load_per_child_in_kg: true,
    platform_material: true,
    bucket_seat: true,
};

const shouldBeShown = (
    feature: Feature,
    features: Features,
    variantAttributes: VariantAttributesWithOptions,
    isSibling: boolean,
): boolean => {
    if (feature.featureName in alwaysShown) {
        return true;
    }
    if (isSibling && feature.featureName in alwaysShownForSiblings) {
        return feature.rawValue(features) !== null;
    }
    return feature.featureName.startsWith("variant_")
        ? feature.hasWorthyValue(variantAttributes)
        : feature.hasWorthyValue(features);
};

type Props = OwnProps;

export class FeaturesList extends React.Component<Props, OwnState> {
    public state: OwnState = {
        expanded: false,
    };

    public render() {
        const { features, variantAttributes, className, brand } = this.props;

        if (!features || !features.type) {
            return null;
        }

        const isSibling = isSiblingType(features);

        // don't show uninteresting values of uninteresting features
        const toBeShownFeatures = LISTED_FEATURE_NAMES.map((name) => {
            const feature = FEATURES_LIST[name];
            if (!feature) {
                throw new Error(`Unknown feature: ${name}`);
            }
            return feature;
        }).filter((feature) => shouldBeShown(feature, features, variantAttributes, isSibling));

        const shownFeatures = this.state.expanded
            ? toBeShownFeatures
            : toBeShownFeatures.slice(0, 9);

        return (
            <div className={cx(styles.FeaturesList, className)}>
                <div className={styles.FeaturesList__items}>
                    <div className={styles.FeaturesList__brand}>
                        <div className={styles.FeaturesList__brand__label}>{STROLLER_BRAND}</div>
                        <Link to={url(strollersListUrl, { slug: brand.slug })}>{brand.name}</Link>
                    </div>

                    {shownFeatures.map((feature, idx) => {
                        return (
                            <FeatureItem
                                key={idx}
                                even={idx % 2 === 0}
                                features={
                                    feature.featureName.startsWith("variant_")
                                        ? variantAttributes
                                        : features
                                }
                                feature={feature}
                            />
                        );
                    })}
                </div>

                <a
                    data-google-interstitial="false"
                    href="#"
                    className={
                        this.state.expanded
                            ? styles.FeaturesList__collapse
                            : styles.FeaturesList__expand
                    }
                    onClick={this.onToggle}
                >
                    {this.state.expanded ? STROLLER_HIDE_FEATURES : STROLLER_MORE_FEATURES}
                </a>
            </div>
        );
    }

    private onToggle = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        this.setState({ expanded: !this.state.expanded });
    };
}
